import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Container,
  Fade,
  TableContainer,
  Avatar,
} from '@mui/material';
import Balance from '../../components/balance';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { useSnackbar } from 'notistack';
import {
  useAddIbanMutation,
  useGetIbansQuery,
  useRemoveIbanMutation,
  useWithdrawalMutation,
  useGetBalanceForSendMoneyQuery,
} from './services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { Controller, useForm } from 'react-hook-form';
import { useCommission } from '../../hooks/useCommission';
import { useMultiplePermissions } from '../../hooks/usePermission';
import TransactionSummaryCard from '../../components/TransactionSummaryCard';
import FintechLoader from '../../components/FintechLoader';
import { Warning, Add as AddIcon, Delete as DeleteIcon, Security as SecurityIcon } from '@mui/icons-material';

const currencyOptions = [
  { value: 1, label: 'TL' },
  { value: 2, label: 'USD' },
  { value: 3, label: 'EUR' },
];

const currencyToForm = (currencyId: number): string => {
  switch (currencyId) {
    case 2:
      return 'USD';
    case 3:
      return 'EUR';
    case 4:
      return 'AU';
    default:
      return 'TL';
  }
};

const formatMoneyInput = (value: string, currencyId: string) => {
  if (!value) return '';

  const currency = currencyToForm(parseInt(currencyId));
  let numericValue = value.replace(/\D/g, '');

  if (!numericValue) return '';

  numericValue = numericValue.padStart(3, '0');
  const integerPart = numericValue.slice(0, -2);
  const decimalPart = numericValue.slice(-2);

  const integerFormatted = new Intl.NumberFormat('tr-TR').format(BigInt(integerPart));
  const symbols: Record<string, string> = { TL: '₺', USD: '$', EUR: '€' };
  const symbol = symbols[currency] || '₺';

  return `${symbol}${integerFormatted},${decimalPart}`;
};

const SendMoneyToBank: React.FC = () => {
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  // Permission kontrollerini hook ile yap
  const permissions = useMultiplePermissions([
    'Corporate.POST.Reading.CüzdandanBankayaParaTransferi',
    'Corporate.POST.Reading.FirmaIBANEklemeYetkisi',
    'Corporate.GET.Reading.FirmaIBANSilmeYetkisi',
  ]);

  const showCuzdandanBankayaParaTransferi =
    permissions['Corporate.POST.Reading.CüzdandanBankayaParaTransferi']?.hasPermission;
  const showfirmaIBANEklemeYetkisi = permissions['Corporate.POST.Reading.FirmaIBANEklemeYetkisi']?.hasPermission;
  const showfirmaIBANSilmeYetkisi = permissions['Corporate.GET.Reading.FirmaIBANSilmeYetkisi']?.hasPermission;
  const permissionLoading =
    permissions['Corporate.POST.Reading.CüzdandanBankayaParaTransferi']?.isLoading ||
    permissions['Corporate.POST.Reading.FirmaIBANEklemeYetkisi']?.isLoading ||
    permissions['Corporate.GET.Reading.FirmaIBANSilmeYetkisi']?.isLoading;

  const shouldSkipBalanceQuery =
    !currentUserState?.companyId || permissionLoading || !showCuzdandanBankayaParaTransferi;

  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    isFetching: isBalanceFetching,
    refetch: refetchBalance,
  } = useGetBalanceForSendMoneyQuery(
    { companyId: currentUserState?.companyId },
    { skip: shouldSkipBalanceQuery }
  );
  const [selectedIban, setSelectedIban] = useState<number | string>('');
  const [form, setForm] = useState({
    value: '',
    currencyId: 1,
    description: '',
  });

  // Add useForm hook for react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      value: '',
      currencyId: 1,
      description: '',
    },
  });
  const [addIbanOpen, setAddIbanOpen] = useState(false);
  const [addIbanForm, setAddIbanForm] = useState({
    receiverIBAN: 'TR',
    receiverFullName: '',
    currencyId: 1,
    customerId: currentUserState?.customerId || 0,
    companyId: currentUserState?.companyId || 0,
  });

  useEffect(() => {
    setAddIbanForm((prev) => ({
      ...prev,
      customerId: currentUserState?.customerId || 0,
      companyId: currentUserState?.companyId || 0,
    }));
  }, [currentUserState?.customerId, currentUserState?.companyId]);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title?: string;
    content?: string;
    onConfirm?: () => void;
  }>({ open: false });
  const { enqueueSnackbar } = useSnackbar();
  const [addIban, { isLoading: addIbanIsLoading }] = useAddIbanMutation();
  const {
    data: getIbans,
    isLoading: isIbansLoading,
    isFetching: isIbansFetching,
    refetch: refetchGetIbans,
  } = useGetIbansQuery({ customerId: currentUserState?.companyId || 0 }, { skip: !currentUserState?.companyId });
  const [removeIban] = useRemoveIbanMutation();
  const watchedCurrency = watch('currencyId');
  const [withdrawal, { isLoading: sendMoneyLoading }] = useWithdrawalMutation();
  const { commission, calculateCommission } = useCommission();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState<any | null>(null);
  const [pendingValue, setPendingValue] = useState<number>(0);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [balanceAutoRefreshKey, setBalanceAutoRefreshKey] = useState<number>(0);

  const handleAddIban = async () => {
    // IBAN validasyonu
    const ibanValue = addIbanForm.receiverIBAN.trim();
    if (!ibanValue || ibanValue === 'TR') {
      enqueueSnackbar('Lütfen geçerli bir IBAN giriniz', { variant: 'warning' });
      return;
    }

    // IBAN uzunluk kontrolü (TR IBAN'ı 26 karakter olmalı)
    if (ibanValue.length !== 26) {
      enqueueSnackbar('IBAN 26 karakter olmalıdır', { variant: 'warning' });
      return;
    }

    // IBAN formatı kontrolü (TR ile başlamalı)
    if (!ibanValue.startsWith('TR')) {
      enqueueSnackbar('IBAN "TR" ile başlamalıdır', { variant: 'warning' });
      return;
    }

    // TR'den sonraki kısmın sadece sayılardan oluştuğunu kontrol et
    const afterTR = ibanValue.slice(2);
    if (!/^\d{24}$/.test(afterTR)) {
      enqueueSnackbar('IBAN TR ifadesinden sonra 24 haneli sayı olmalıdır', { variant: 'warning' });
      return;
    }

    // Ad Soyad/Unvan validasyonu
    const fullName = addIbanForm.receiverFullName.trim();
    if (!fullName) {
      enqueueSnackbar('Lütfen Ad Soyad/Unvan bilgisini giriniz', { variant: 'warning' });
      return;
    }

    // Ad Soyad/Unvan minimum uzunluk kontrolü
    if (fullName.length < 3) {
      enqueueSnackbar('Ad Soyad/Unvan en az 3 karakter olmalıdır', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const result = await addIban({
        ...addIbanForm,
        customerId: currentUserState?.customerId || 0,
        companyId: currentUserState?.companyId || 0,
      });
      if (result.data && result.data.success) {
        enqueueSnackbar('İban Eklendi', { variant: 'success' });
        setAddIbanOpen(false);
        setAddIbanForm({
          receiverIBAN: 'TR',
          receiverFullName: '',
          customerId: currentUserState?.customerId || 0,
          companyId: currentUserState?.companyId || 0,
          currencyId: 1,
        });
        refetchGetIbans();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleRemoveIban = (id: number, receiverIBAN: string, receiverFullName: string) => {
    setConfirmModal({
      open: true,
      title: 'IBAN Silme',
      content: `“${receiverFullName}” adlı “${receiverIBAN}”  IBAN'ı silmek istiyor musunuz ?`,
      onConfirm: async () => {
        setLoading(true);

        const result = await removeIban({ id: id });
        if (result?.data && result?.data?.success) {
          enqueueSnackbar(result?.data?.message, { variant: 'success' });
          refetchGetIbans();
        }
        setLoading(false);
        setConfirmModal({ open: false });
      },
    });
  };

  const confirmSendMoney = async () => {
    try {
      const result = await withdrawal({
        data: {
          currencyId: 1, // Sadece TL kullanılacak
          value: pendingValue,
          description: form.description ?? '',
          valueFee: commission?.valueFee || 0,
          ibanId: selectedIban as number,
        },
        companyId: currentUserState?.companyId,
        customerId: currentUserState?.customerId,
      });
      setConfirmDialogOpen(false);
      if (result.data && result.data.success) {
        reset();
        setForm({
          value: '',
          currencyId: 1,
          description: '',
        });
        setSelectedIban('');
        // Upper `Balance` component uses `/Company/GetBalance`, so trigger it to refetch.
        setBalanceAutoRefreshKey((k) => k + 1);
        enqueueSnackbar(result.data.message, { variant: 'success' });
      }
    } catch (error) { }
  };

  const onSubmit = async (data: any) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const formattedValue = parseFloat(
      data.value.replace('₺', '').replace('$', '').replace('€', '').replaceAll('.', '').replace(',', '.')
    );

    // Komisyonu hesapla
    await calculateCommission({
      value: formattedValue,
      currencyId: '1', // Sadece TL kullanılacak
      type: '3', // veya uygun tip neyse onu kullanın
      companyId: currentUserState?.companyId,
      customerId: currentUserState?.customerId,
    });

    // Komisyon hesaplandıktan sonra bakiye kontrolü yap
    try {
      // Bakiye verisini yeniden çek
      if (shouldSkipBalanceQuery) {
        enqueueSnackbar('Bakiye bilgisi alınamadı. Lütfen sayfanın yüklenmesini bekleyip tekrar deneyin.', {
          variant: 'warning',
        });
        setIsSubmitting(false);
        return;
      }

      const balanceResult = await refetchBalance();
      const currentBalance = balanceResult?.data?.data?.totalBalance || 0;
      const totalAmount = formattedValue + (commission?.valueFee || 0);

      // Bakiye yeterliyse işlemi devam ettir
      data.receiverIBAN = getIbans?.data?.find((iban: any) => iban.id === selectedIban)?.receiverIBAN || '';

      setIsModalContentLoading(true);
      setConfirmDialogOpen(true);
      setIsSubmitting(false);

      setTimeout(() => {
        setPendingTransfer(data);
        setPendingValue(formattedValue);
        setIsModalContentLoading(false);
      }, 2000);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const formatMoney = (value: number, currency: string | number = '1'): string => {
    const currencyStr = typeof currency === 'number' ? currency.toString() : currency;
    const currencyLabel = currencyToForm(parseInt(currencyStr));
    const formatter = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyLabel === 'TL' ? 'TRY' : currencyLabel,
    });
    return formatter.format(value);
  };

  // Tüm loading state'lerini ve veri yoksa loader'ı göster
  const isLoading =
    permissionLoading ||
    isBalanceLoading ||
    isBalanceFetching ||
    isIbansLoading ||
    isIbansFetching ||
    !balanceData ||
    !getIbans;

  const [isModalContentLoading, setIsModalContentLoading] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      // Tüm veri ve HTML yüklendikten sonra kısa bir gecikme ile fade başlat
      const timer = setTimeout(() => setPageLoaded(true), 200); // 200ms gecikme ile fade başlasın
      return () => clearTimeout(timer);
    } else {
      setPageLoaded(false);
    }
  }, [isLoading]);

  if (!permissionLoading && !showCuzdandanBankayaParaTransferi) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Card sx={{
          maxWidth: 500,
          p: 4,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(252, 165, 165, 0.3)',
          textAlign: 'center'
        }}>
          <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
            Erişim Engellendi
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
            Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Fade in={!isLoading} timeout={600}>
        <Container
          maxWidth={false}
          sx={{
            flexGrow: 1,
            padding: { xs: '110px 8px 16px 8px', sm: '110px 16px 24px 16px', md: '110px 32px 40px 32px', lg: '110px 40px 40px 40px' },
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          {/* Main Dashboard Container */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              mb: { xs: 2, sm: 3 },
              mx: 'auto',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '6px',
                background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
              },
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
                borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
              }}
            >
              <Box sx={{
                px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 },
                pt: { xs: 3, sm: 4 },
                pb: { xs: 2, sm: 3 },
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}>
                <Balance autoRefreshKey={balanceAutoRefreshKey} />
              </Box>
              <Box sx={{
                px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 },
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}>
                <ConfirmationModal
                  isOpen={confirmDialogOpen}
                  onClose={() => setConfirmDialogOpen(false)}
                  isLoading={sendMoneyLoading}
                  onConfirm={confirmSendMoney}
                  title="Banka Hesabına Para Gönder"
                  confirmButtonDisabled={sendMoneyLoading || isModalContentLoading}
                  confirmButtonText="Gönder"
                  closeButtonText="İptal"
                  optionalWidth="800px !important"
                >
                  {isModalContentLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', py: 4 }}>
                      <FintechLoader />
                    </Box>
                  ) : pendingTransfer ? (
                    <TransactionSummaryCard
                      firstFieldName="Alıcı IBAN"
                      walletNo={pendingTransfer?.receiverIBAN}
                      value={pendingTransfer?.value}
                      commissionValue={commission?.valueFee || 0}
                      totalValue={commission?.total || 0}
                      currencyLabel={currencyToForm(Number(pendingTransfer?.currency))}
                      formatMoney={formatMoney}
                    />
                  ) : null}
                </ConfirmationModal>
                <ConfirmationModal
                  isOpen={confirmModal.open}
                  title={confirmModal.title}
                  content={confirmModal.content}
                  onClose={() => setConfirmModal({ open: false })}
                  onConfirm={confirmModal.onConfirm}
                  confirmButtonText="Sil"
                  closeButtonText="Hayır"
                  isLoading={loading}
                  customHeaderStyle={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    titleColor: 'white',
                    icon: (
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          width: 32,
                          height: 32,
                        }}
                      >
                        <Warning fontSize="small" />
                      </Avatar>
                    ),
                  }}
                />
                {/* IBAN Ekle Modal */}
                <ConfirmationModal
                  isOpen={addIbanOpen}
                  title="IBAN Ekle"
                  onClose={() => setAddIbanOpen(false)}
                  onConfirm={handleAddIban}
                  confirmButtonText="IBAN Ekle"
                  closeButtonText="Vazgeç"
                  isLoading={addIbanIsLoading}
                  optionalWidth="500px"
                >
                  <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="IBAN"
                      value={addIbanForm.receiverIBAN}
                      onChange={(e) => {
                        const input = e.target.value.toUpperCase().replace(/\s/g, '');
                        // TR'den sonraki kısmın sadece sayı olmasını sağla
                        if (input.startsWith('TR')) {
                          const afterTR = input.slice(2).replace(/\D/g, ''); // Sadece rakamları al
                          const newValue = 'TR' + afterTR;
                          setAddIbanForm({
                            ...addIbanForm,
                            receiverIBAN: newValue.substring(0, 26),
                          });
                        } else if (input === 'T' || input === '') {
                          // T veya boş ise TR yap
                          setAddIbanForm({
                            ...addIbanForm,
                            receiverIBAN: 'TR',
                          });
                        }
                      }}
                      required
                    />
                    <TextField
                      label="Ad Soyad / Unvan"
                      value={addIbanForm.receiverFullName}
                      onChange={(e) => setAddIbanForm({ ...addIbanForm, receiverFullName: e.target.value })}
                      required
                    />
                    <FormControl>
                      <InputLabel>Para Birimi</InputLabel>
                      <Select
                        value={addIbanForm.currencyId}
                        label="Para Birimi"
                        onChange={(e) => setAddIbanForm({ ...addIbanForm, currencyId: Number(e.target.value) })}
                      >
                        {currencyOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </ConfirmationModal>
              </Box>
              {/* Para Gönder Formu */}
              {showCuzdandanBankayaParaTransferi && (
                <Box sx={{
                  px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 },
                  pb: { xs: 2, sm: 3 },
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflowX: 'hidden'
                }}>
                  <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3, mt: { xs: 3, sm: 4, md: 5 } }}>
                    {/* Send Money Form */}
                    <Fade in={true} timeout={800}>
                      <Grid item xs={12} lg={6} sx={{ mb: { xs: 2, sm: 0 }, width: '100%' }}>
                        <Box
                          sx={{
                            flex: 1,
                            bgcolor: 'background.paper',
                            borderRadius: { xs: 1.5, sm: 2 },
                            maxHeight: { xs: 'none', lg: 502 },
                            overflowY: 'auto',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              borderBottom: 1,
                              padding: { xs: '12px', sm: '15px' },
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              Banka Hesabına Para Gönder
                            </Typography>
                          </Box>
                          <Box sx={{
                            p: { xs: 1.5, sm: 2 },
                            flex: 1,
                            overflow: 'auto',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}>
                            <form onSubmit={handleSubmit(onSubmit)}>
                              <FormControl fullWidth sx={{ mb: 2, width: '100%' }}>
                                <InputLabel>Alıcı IBAN</InputLabel>
                                <Select
                                  value={selectedIban || ''}
                                  label="Alıcı IBAN"
                                  onChange={(e) => {
                                    setSelectedIban(Number(e.target?.value));
                                  }}
                                  required
                                >
                                  {getIbans?.data &&
                                    getIbans?.data?.map((iban: any) => (
                                      <MenuItem key={iban.id} value={iban.id}>
                                        {iban.receiverIBAN} -- {iban.receiverFullName} -- {iban.receiverBankName}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                              {/* Tutar Alanı - Validasyon ve Para Formatlama ile */}
                              <Controller
                                name="value"
                                control={control}
                                rules={{
                                  required: 'Lütfen göndermek istediğiniz tutarı giriniz.',
                                  validate: (value) => {
                                    const numValue = parseFloat(
                                      value
                                        .replace('₺', '')
                                        .replace('$', '')
                                        .replace('€', '')
                                        .replaceAll('.', '')
                                        .replace(',', '.')
                                    );
                                    return numValue >= 1 || 'Tutar 1,00 ve üzeri olmalıdır';
                                  },
                                }}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    label="Tutar"
                                    placeholder="Tutar Giriniz"
                                    required
                                    error={!!errors?.value}
                                    helperText={errors?.value?.message}
                                    onChange={(e) => {
                                      const input = e.target as HTMLInputElement;
                                      const digits = input.value.replace(/\D/g, '');
                                      if (digits.length > 16) return;
                                      const formatted = formatMoneyInput(input.value, '1'); // Sadece TL formatı
                                      field.onChange(formatted);
                                      requestAnimationFrame(() => {
                                        input.setSelectionRange(formatted.length, formatted.length);
                                      });
                                    }}
                                    onFocus={(e) => {
                                      const input = e.target as HTMLInputElement;
                                      requestAnimationFrame(() => {
                                        input.setSelectionRange(input.value.length, input.value.length);
                                      });
                                    }}
                                    onPaste={(e) => {
                                      e.preventDefault();
                                      const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 16);
                                      if (!digits) return;
                                      const formatted = formatMoneyInput(digits, '1');
                                      field.onChange(formatted);
                                      const input = e.target as HTMLInputElement;
                                      requestAnimationFrame(() => {
                                        input.setSelectionRange(formatted.length, formatted.length);
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Backspace' || e.key === 'Delete') {
                                        e.preventDefault();
                                        const input = e.target as HTMLInputElement;
                                        const value = input.value;
                                        const cursorPos = input.selectionStart ?? value.length;
                                        const selEnd = input.selectionEnd ?? cursorPos;
                                        const digits = value.replace(/\D/g, '');
                                        let newDigits: string;
                                        if (selEnd > cursorPos) {
                                          const digitsBeforeStart = value.slice(0, cursorPos).replace(/\D/g, '').length;
                                          const digitsBeforeEnd = value.slice(0, selEnd).replace(/\D/g, '').length;
                                          newDigits = digits.slice(0, digitsBeforeStart) + digits.slice(digitsBeforeEnd);
                                        } else if (e.key === 'Backspace') {
                                          let digitIndex = -1;
                                          for (let i = cursorPos - 1; i >= 0; i--) {
                                            if (/\d/.test(value[i])) {
                                              digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                                              break;
                                            }
                                          }
                                          if (digitIndex === -1) return;
                                          newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                                        } else {
                                          let digitIndex = -1;
                                          for (let i = cursorPos; i < value.length; i++) {
                                            if (/\d/.test(value[i])) {
                                              digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                                              break;
                                            }
                                          }
                                          if (digitIndex === -1) return;
                                          newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                                        }
                                        const formatted = formatMoneyInput(newDigits, '1');
                                        field.onChange(formatted);
                                        requestAnimationFrame(() => {
                                          input.setSelectionRange(formatted.length, formatted.length);
                                        });
                                        return;
                                      }
                                      const allowedKeys = ['Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                      if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9') && !e.ctrlKey && !e.metaKey) {
                                        e.preventDefault();
                                      }
                                    }}
                                  />
                                )}
                              />
                              <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Para Birimi</InputLabel>
                                <Select
                                  value={1} // Sadece TL (value: 1)
                                  label="Para Birimi"
                                  disabled={true} // Para birimi seçimi kapalı
                                  required
                                >
                                  <MenuItem value={1}>TL</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                label="Açıklama"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mb: 2 }}
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  type="submit"
                                  disabled={isSubmitting}
                                  sx={{
                                    width: '120px',
                                    height: '36px',
                                    fontSize: '0.875rem',
                                    position: 'relative',
                                  }}
                                >
                                  {isSubmitting ? <CircularProgress size={16} color="inherit" />  : 'Gönder'}
                                </Button>
                              </Box>
                            </form>
                          </Box>
                        </Box>
                      </Grid>
                    </Fade>
                    {/* IBAN Listesi */}
                    <Fade in={true} timeout={800}>
                      <Grid item xs={12} lg={6} sx={{ mb: { xs: 2, sm: 0 }, width: '100%' }}>
                        <Box
                          sx={{
                            flex: 1,
                            bgcolor: 'background.paper',
                            borderRadius: { xs: 1.5, sm: 2 },
                            maxHeight: { xs: 'none', lg: 502 },
                            overflowY: 'auto',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              borderBottom: 1,
                              padding: { xs: '12px', sm: '15px' },
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: { xs: 'wrap', sm: 'nowrap' },
                              gap: { xs: 1, sm: 0 },
                            }}
                          >
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>IBAN Listesi</Typography>
                            {showfirmaIBANEklemeYetkisi && (
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon />}
                                onClick={() => setAddIbanOpen(true)}
                                sx={{
                                  width: '120px',
                                  height: '36px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.25)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.35)',
                                  }
                                }}
                              >
                                IBAN Ekle
                              </Button>
                            )}
                          </Box>
                          <Box sx={{
                            p: { xs: 1.5, sm: 2.5 },
                            flex: 1,
                            overflow: 'auto',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}>
                            <TableContainer sx={{
                              maxHeight: { xs: 'none', lg: 380 },
                              width: '100%',
                              overflowX: 'auto'
                            }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}
                                    >
                                      Ad/Soyad
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}
                                    >
                                      Banka Adı
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}
                                    >
                                      IBAN
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}
                                    >
                                      Para Birimi
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}
                                    >
                                      Kayıt Tarihi
                                    </TableCell>
                                    {showfirmaIBANSilmeYetkisi && <TableCell>Sil</TableCell>}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {getIbans?.data?.map((iban: any) => (
                                    <TableRow key={iban.id}>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                          {iban.receiverFullName}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                          {iban.receiverBankName}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                          {iban.receiverIBAN}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                          {currencyOptions.find((c) => c.value === iban.currencyId)?.label}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                          {new Date(iban.create_Date).toLocaleString('tr-TR')}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {showfirmaIBANSilmeYetkisi && (
                                          <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            onClick={() =>
                                              handleRemoveIban(iban.id, iban.receiverIBAN, iban.receiverFullName)
                                            }
                                            sx={{
                                              fontSize: '0.75rem',
                                              fontWeight: 600,
                                              textTransform: 'none',
                                              borderRadius: 1.5,
                                              px: 1.5,
                                              py: 0.5,
                                              minWidth: 'auto',
                                              boxShadow: '0 2px 6px rgba(211, 47, 47, 0.25)',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 10px rgba(211, 47, 47, 0.35)',
                                              }
                                            }}
                                          >
                                            Sil
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Box>
                      </Grid>
                    </Fade>
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </Container>
      </Fade>
    </>
  );
};

export default SendMoneyToBank;
